import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Box, Button, Typography, Grid, Card, Avatar, Chip } from '@mui/material';
import {
  AccountBalance, Inventory, People, Payment, Assessment, Language,
  Business, Security, Speed, CheckCircle, TrendingUp, AutoAwesome, BarChart,
  PointOfSale, Loyalty, AccountTree
} from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import DashboardMockup from '../../components/Website/DashboardMockup';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('home');

  const modules = [
    { icon: <AccountBalance />, title: t('website.home.modules.financials'), desc: t('website.home.modules.financialsDesc') },
    { icon: <Inventory />, title: t('website.home.modules.supplyChain'), desc: t('website.home.modules.supplyChainDesc') },
    { icon: <PointOfSale />, title: t('website.home.modules.omnichannelSales'), desc: t('website.home.modules.omnichannelSalesDesc') },
    { icon: <Business />, title: t('website.home.modules.manufacturing'), desc: t('website.home.modules.manufacturingDesc') },
    { icon: <People />, title: t('website.home.modules.crmErp'), desc: t('website.home.modules.crmErpDesc') },
    { icon: <Payment />, title: t('website.home.modules.hcmPayroll'), desc: t('website.home.modules.hcmPayrollDesc') },
    { icon: <AccountTree />, title: t('website.home.modules.consolidation'), desc: t('website.home.modules.consolidationDesc') },
    { icon: <AutoAwesome />, title: t('website.home.modules.aiInsights'), desc: t('website.home.modules.aiInsightsDesc') }
  ];

  const steps = [
    { icon: <Business />, title: t('website.home.whyFlux.configureGovernance'), desc: t('website.home.whyFlux.configureGovernanceDesc') },
    { icon: <Speed />, title: t('website.home.whyFlux.orchestrateWorkflows'), desc: t('website.home.whyFlux.orchestrateWorkflowsDesc') },
    { icon: <TrendingUp />, title: t('website.home.whyFlux.analyzeScale'), desc: t('website.home.whyFlux.analyzeScaleDesc') }
  ];

  const features = [
    'MFA & SSO Support',
    'Immutable Audit Trails',
    'IFRS & UK GAAP Ready',
    'Automated Consolidation',
    'Asset Capitalization',
    'Trade Letters of Credit',
    'Budgeting & Forecasts',
    'IFRS 16 Lease Accounting',
    'BNPL & Installment Sales (IFRS 15/9)'
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip={t('website.home.chip')} py={{ xs: 6, md: 10 }} minHeight="480px">
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">

          {/* ── left: text + CTAs ── */}
          <Grid item xs={12} md={6}>
            <Typography variant="h1" sx={{ fontSize: { xs: 34, md: 52 }, fontWeight: 800, mb: 2.5, lineHeight: 1.15, color: 'primary.contrastText' }}>
              {t('website.home.heroTitle')}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.85, fontWeight: 400, lineHeight: 1.65, color: 'primary.contrastText', fontSize: { xs: 15, md: 17 } }}>
              {t('website.home.heroSubtitle')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button component={Link} to="/register" variant="contained"
                sx={{
                  bgcolor: 'primary.contrastText', color: 'primary.main', px: 3.5, py: 1.5, borderRadius: 2, fontWeight: 700, transition: 'all 0.3s',
                  '&:hover': { bgcolor: 'rgba(22,51,0,0.9)', color: '#9FE870', transform: 'translateY(-2px)', boxShadow: '0 8px 32px rgba(22,51,0,0.25)' }
                }}>
                {t('website.home.deployNow')}
              </Button>
              <Button component={Link} to="/demo" variant="outlined"
                sx={{
                  borderColor: 'primary.contrastText', color: 'primary.contrastText', borderWidth: 2, px: 3.5, py: 1.5, borderRadius: 2, fontWeight: 700, transition: 'all 0.3s',
                  '&:hover': { borderColor: 'primary.contrastText', bgcolor: 'rgba(22,51,0,0.08)', borderWidth: 2, transform: 'translateY(-2px)' }
                }}>
                {t('website.home.scheduleDemo')}
              </Button>
            </Box>
          </Grid>

          {/* ── right: animated dashboard mockup ── */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <DashboardMockup />
          </Grid>

        </Grid>
      </HeroSection>

      {/* Trusted By Section */}
      <Box sx={{ py: 6, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary', mb: 4 }}>
            {t('website.home.trustedBy')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {[
              { src: '/assets/client-logos/core-media.png', alt: 'Core Media' },
              { src: '/assets/client-logos/dutco.png', alt: 'Dutco' },
              { src: '/assets/client-logos/nsstc.png', alt: 'NSSTC' },
              { src: '/assets/client-logos/softtns.png', alt: 'Softtns' },
              { src: '/assets/client-logos/teevision-printing.png', alt: 'Teevision Printing' },
            ].map((logo) => (
              <Box
                key={logo.alt}
                sx={{
                  width: 120, height: 60,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0.7,
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 1 }
                }}
              >
                <Box component="img" src={logo.src} alt={logo.alt} loading="lazy" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Core Modules */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
            {t('website.home.modules.title')}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}>
            {t('website.home.modules.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {modules.map((module, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  border: '1px solid',
                  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                  boxShadow: 'none',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(22, 51, 0, 0.12)',
                    transform: 'translateY(-4px)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'secondary.main',
                    width: 56,
                    height: 56,
                    mb: 2
                  }}
                >
                  {module.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {module.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {module.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
              {t('website.home.whyFlux.title')}
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            {steps.map((step, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 3,
                      bgcolor: 'secondary.main',
                      color: 'primary.main',
                      fontSize: 32,
                      fontWeight: 800
                    }}
                  >
                    {idx + 1}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Integrations Section */}
      <Box sx={{ py: 10, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 42 }, fontWeight: 800, mb: 2 }}>
              {t('website.home.integrations.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
              {t('website.home.integrations.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {[
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-ebay.svg', alt: 'eBay' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-wix.svg', alt: 'Wix' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-shopify.svg', alt: 'Shopify' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-square.svg', alt: 'Square' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-paypal-2x.png', alt: 'PayPal' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-amazon.svg', alt: 'Amazon' },
              { src: 'https://digitalasset.intuit.com/render/content/dam/intuit/sbseg/en_us/quickbooks-online/web/image/logo-box-etsy-2x.png', alt: 'Etsy' },
            ].map((logo) => (
              <Box
                key={logo.alt}
                sx={{
                  width: 96, height: 96,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                  borderRadius: 3,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  p: 1.5,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }
                }}
              >
                <Box component="img" src={logo.src} alt={logo.alt} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Security & Compliance */}
      <Box sx={{ py: 10, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 40 }, fontWeight: 800, mb: 2 }}>
                  {t('website.home.security.title')}
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  {t('website.home.security.subtitle')}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {t('website.home.security.features', { returnObjects: true })?.map((feature, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircle sx={{ color: 'success.main' }} />
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop"
                alt="Enterprise Security"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: 10, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'primary.main', color: 'primary.contrastText', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 }, fontWeight: 800, mb: 3 }}>
            {t('website.home.cta.title')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, opacity: 0.9, fontWeight: 400 }}>
            {t('website.home.cta.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={Link} to="/register" variant="contained"
              sx={{
                bgcolor: 'primary.contrastText', color: 'primary.main', px: 4, py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '1.1rem',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' }
              }}>
              {t('website.home.cta.startTrial')}
            </Button>
            <Button component={Link} to="/contact" variant="outlined"
              sx={{
                borderColor: 'primary.contrastText', color: 'primary.contrastText', borderWidth: 2, px: 4, py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '1.1rem',
                '&:hover': { borderColor: 'primary.contrastText', bgcolor: 'rgba(255,255,255,0.1)', borderWidth: 2, transform: 'translateY(-2px)' }
              }}>
              {t('website.home.cta.contactSales')}
            </Button>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default HomePage;
